import axios, {
  AxiosHeaders,
  AxiosInstance,
  ParamsSerializerOptions,
  RawAxiosRequestConfig,
} from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import qs from "qs";

import {
  accessTokenAtom,
  showsSessionExpiredModalAtom,
  store,
} from "@/utils/atoms";
import { refreshAuthorization } from "@/utils/authentication";

export const generateApiInstance = <T>(
  baseURL: string,
  config: RawAxiosRequestConfig,
  options?: RawAxiosRequestConfig,
): Promise<T> => {
  const axiosInstance = generateAxiosInstance(baseURL);
  const token = store.get(accessTokenAtom);

  // If we have headers we want to add the Authorization header
  if (options?.headers !== undefined) {
    const headers = new AxiosHeaders(options.headers as any);
    const skipAuth =
      `${headers.get("X-Skip-Auth") ?? ""}`.toLowerCase() === "true";

    // Ensure this internal flag is never forwarded to the backend.
    headers.delete("X-Skip-Auth");

    // If Authorization was explicitly provided we don't override it.
    if (!headers.get("Authorization") && token && !skipAuth) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return axiosInstance({
      ...config,
      ...options,
      headers,
    }).then(({ data }) => data);
  }
  // Else we create new headers for this request
  else {
    const headers = token
      ? new AxiosHeaders({ Authorization: `Bearer ${token}` })
      : new AxiosHeaders();

    return axiosInstance({
      ...config,
      ...options,
      headers,
    }).then(({ data }) => data);
  }
};

export const generateAxiosInstance: (baseURL: string) => AxiosInstance = (
  baseURL: string,
) => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    paramsSerializer: {
      serialize: (params: ParamsSerializerOptions) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
    },
  });

  // Instantiate the interceptor for refresh on 401
  createAuthRefreshInterceptor(instance, refreshAuthorization);

  // Add response interceptor to prevent other errors when session expired modal is shown
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // If session expired modal is already shown, prevent other error handling
      if (store.get(showsSessionExpiredModalAtom)) {
        // Return a resolved promise to prevent further error propagation
        return Promise.resolve({ data: null, status: 200 });
      }

      return Promise.reject(error);
    },
  );

  return instance;
};
